/**
 * features/landing/BrowserFrame.tsx
 * ------------------------------------------------------------------
 * The device used throughout the company page: a browser window with
 * chrome, a URL pill, and a screen.
 *
 * The screen shows `/work/<image>` when that file exists in `public/`.
 * When it doesn't (or fails to load) it falls back to an abstract,
 * on-brand UI mock tinted with the project's accent colour — so the
 * page never shows a broken image and looks deliberate before any
 * screenshots are taken.
 * ------------------------------------------------------------------
 */
import { useState } from 'react';

/** A dashboard-shaped placeholder drawn in the project's accent colour. */
function MockScreen({ accent }: { accent: string }) {
  const bars = [92, 74, 61, 84, 48];
  return (
    <div className="ac-mock" style={{ ['--acc' as string]: accent }}>
      <div className="ac-mock-rail">
        <span className="ac-mock-brand" />
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <span key={i} className={`ac-mock-nav${i === 1 ? ' on' : ''}`} />
        ))}
      </div>
      <div className="ac-mock-body">
        <div className="ac-mock-kpis">
          <div className="ac-mock-kpi">
            <span className="ac-mock-kpi-l" />
            <span className="ac-mock-kpi-v" />
          </div>
          <div className="ac-mock-kpi">
            <span className="ac-mock-kpi-l" />
            <span className="ac-mock-kpi-v alt" />
          </div>
          <div className="ac-mock-kpi">
            <span className="ac-mock-kpi-l" />
            <span className="ac-mock-kpi-v" />
          </div>
        </div>
        <div className="ac-mock-chart">
          {bars.map((h, i) => (
            <span key={i} style={{ height: `${h}%` }} />
          ))}
        </div>
        <div className="ac-mock-rows">
          {[0, 1, 2].map((i) => (
            <div key={i} className="ac-mock-row">
              <span />
              <span />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function BrowserFrame({
  image,
  src,
  domain,
  accent,
  alt,
  className = '',
}: {
  /** Fallback filename under `public/work/`. */
  image: string;
  /** Uploaded image URL from the media library; wins when present. */
  src?: string;
  domain: string;
  accent: string;
  alt: string;
  className?: string;
}) {
  // Track WHICH source failed, not just that one did — otherwise an
  // uploaded image arriving after the fallback 404s would stay hidden.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const source = src || `/work/${image}`;
  const failed = failedSrc === source;

  return (
    <div className={`ac-win ${className}`}>
      <div className="ac-win-bar">
        <span className="ac-win-dots">
          <i />
          <i />
          <i />
        </span>
        <span className="ac-win-url">{domain}</span>
      </div>
      <div className="ac-win-screen">
        {failed ? (
          <MockScreen accent={accent} />
        ) : (
          <img
            key={source}
            src={source}
            alt={alt}
            loading="lazy"
            decoding="async"
            onError={() => setFailedSrc(source)}
          />
        )}
      </div>
    </div>
  );
}
